import React, { useCallback, useContext, useEffect, useState } from 'react'
import AuthContext from '../context/AuthContext'
import {
    Box, List, ListItem, ListItemText, ListItemIcon,
    IconButton, Typography, CircularProgress
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import iconUrl from 'leaflet/dist/images/marker-icon.png'
import iconShadowUrl from 'leaflet/dist/images/marker-shadow.png'

const DefaultIcon = L.icon({
    iconUrl,
    shadowUrl: iconShadowUrl,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
})
L.Marker.prototype.options.icon = DefaultIcon

function FitBounds({ positions }) {
    const map = useMap()
    useEffect(() => {
        if (positions.length > 0) {
            const bounds = L.latLngBounds(positions)
            map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 })
        }
    }, [map, positions])
    return null
}

const StudentAddressesComponent = ({ studentId }) => {
    const { authTokens, logoutUser, backendUrl } = useContext(AuthContext)
    const [addresses, setAddresses] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        getAddresses()
    }, [])

    const handleDelete = async (id) => {
        const response = await fetch(backendUrl + 'api/delete-student-poi/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + String(authTokens.access)
            },
            body: JSON.stringify({ id })
        })
        if (response.status === 200) {
            setAddresses(prev => prev.filter(a => a.id !== id))
        } else if (response.status === 401) {
            logoutUser()
        }
    }

    const getAddresses = useCallback(async () => {
        const response = await fetch(backendUrl + 'api/get-student-pois/' + studentId, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + String(authTokens.access)
            }
        })
        if (response.status === 200) {
            const data = await response.json()
            setAddresses(data.map(ad => ({
                id: ad.id,
                name: ad.name,
                address: ad.address,
                latitude: ad.latitude ? parseFloat(ad.latitude) : null,
                longitude: ad.longitude ? parseFloat(ad.longitude) : null,
            })))
        } else if (response.status === 401) {
            logoutUser()
        }
        setLoading(false)
    }, [])

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
            </Box>
        )
    }

    if (addresses.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                No addresses added yet.
            </Typography>
        )
    }

    const mappableAddresses = addresses.filter(a => a.latitude && a.longitude)

    return (
        <Box>
            <List dense disablePadding>
                {addresses.map(ad => (
                    <ListItem
                        key={ad.id}
                        disableGutters
                        secondaryAction={
                            <IconButton
                                edge="end"
                                aria-label="delete"
                                size="small"
                                onClick={() => handleDelete(ad.id)}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        }
                    >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <LocationOnIcon fontSize="small" color="action" />
                        </ListItemIcon>
                        <ListItemText
                            primary={ad.address}
                            secondary={ad.name || null}
                        />
                    </ListItem>
                ))}
            </List>
            {mappableAddresses.length > 0 && (
                <Box sx={{ mt: 2, borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                    <MapContainer
                        center={[mappableAddresses[0].latitude, mappableAddresses[0].longitude]}
                        zoom={14}
                        style={{ height: 240, width: '100%' }}
                        scrollWheelZoom={false}
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <FitBounds positions={mappableAddresses.map(a => [a.latitude, a.longitude])} />
                        {mappableAddresses.map(ad => (
                            <Marker key={ad.id} position={[ad.latitude, ad.longitude]}>
                                <Popup>{ad.name ? `${ad.name}: ` : ''}{ad.address}</Popup>
                            </Marker>
                        ))}
                    </MapContainer>
                </Box>
            )}
        </Box>
    )
}

export default StudentAddressesComponent
